import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import CheckIcon from "@mui/icons-material/Check";
import PhonelinkRingIcon from "@mui/icons-material/PhonelinkRing";
import { useDeepgramASR } from "../components/turno/hooks/useDeepgramASR";
import { mensajeErrorMicrofono } from "../components/turno/hooks/mensajeErrorMicrofono";
import { mandarTramo, cerrarPedido, tomarPedido } from "../helpers/mic-remoto";
import { anotarDictado, descartarDictado } from "../helpers/dictado-buffer";
import getClient from "../helpers/get-client";

/**
 * MODO MICRÓFONO — el celular convertido en micrófono del computador.
 *
 * Pantalla completa, sin el Modo Turno detrás. El médico no tiene que ver ni
 * tocar la ficha acá: la ficha está en el computador, y ahí es donde el texto se
 * coloca, en la casilla donde él tenga el cursor.
 *
 * Lo que hace esta pantalla es UNA cosa: escuchar y mandar tramos. Cada vez que
 * el médico deja de hablar (`speech_final`), el tramo sale para el computador.
 * Por eso puede dictar una frase, callarse, mover el cursor allá, y seguir
 * hablando sin tocar nada acá.
 *
 * El único gesto que se le pide es el primero, y no se puede evitar: ningún
 * navegador enciende el micrófono sin un toque del usuario en este aparato.
 */
const ModoMicrofono = ({ pedido, onSalir }) => {
  const [tramos, setTramos] = useState([]);
  const [enVivo, setEnVivo] = useState("");
  const [error, setError] = useState("");
  const [keyterms, setKeyterms] = useState([]);
  const [tomado, setTomado] = useState(false);
  const pedidoRef = useRef(pedido);
  pedidoRef.current = pedido;

  useEffect(() => {
    let vivo = true;
    getClient()
      .get("/api/medical_analysis/asr/config/")
      .then((r) => { if (vivo) setKeyterms(r.data?.deepgram_keyterms || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  const subirTramo = useCallback(async (texto) => {
    const limpio = String(texto || "").trim();
    if (!limpio || !pedidoRef.current?.id) return;
    // Lo PRIMERO es anotarlo en este aparato. Antes, un tramo que no lograba
    // subir quedaba marcado "falló" en pantalla y moría con la pestaña: el
    // médico había dictado una frase que ya no existía en ningún lado.
    let idDiario = null;
    try {
      idDiario = anotarDictado({
        texto: limpio,
        tab: pedidoRef.current?.tab,
        row: pedidoRef.current?.row,
        column: pedidoRef.current?.column,
        origen: "mic_remoto",
      });
    } catch (_) {}
    // Optimista: el médico ve que su frase salió sin esperar a la red. Si el
    // envío falla se marca, pero nunca se le borra de la pantalla lo que dijo.
    const local = { id: `local-${Date.now()}`, texto: limpio, estado: "enviando", idDiario };
    setTramos((prev) => [...prev, local]);
    try {
      await mandarTramo(pedidoRef.current.id, limpio);
      // Llegó al servidor: el respaldo local ya no hace falta.
      if (idDiario) { try { descartarDictado(idDiario); } catch (_) {} }
      setTramos((prev) => prev.map((t) => (t.id === local.id ? { ...t, estado: "enviado" } : t)));
    } catch (_) {
      // Se queda en el diario a propósito, para que sobreviva a una recarga.
      setTramos((prev) => prev.map((t) => (t.id === local.id ? { ...t, estado: "falló" } : t)));
      setError("Un tramo no llegó al computador. Tocá para reintentar.");
    }
  }, []);

  /** Reintenta los tramos que no llegaron. Nada se descarta hasta que el
   *  servidor confirma. */
  const reintentarFallidos = useCallback(async () => {
    const fallidos = tramos.filter((t) => t.estado === "falló");
    if (!fallidos.length || !pedidoRef.current?.id) return;
    setError("");
    for (const t of fallidos) {
      setTramos((prev) => prev.map((x) => (x.id === t.id ? { ...x, estado: "enviando" } : x)));
      try {
        await mandarTramo(pedidoRef.current.id, t.texto);
        if (t.idDiario) { try { descartarDictado(t.idDiario); } catch (_) {} }
        setTramos((prev) => prev.map((x) => (x.id === t.id ? { ...x, estado: "enviado" } : x)));
      } catch (_) {
        setTramos((prev) => prev.map((x) => (x.id === t.id ? { ...x, estado: "falló" } : x)));
        setError("Todavía no llega al computador. Revisá la conexión.");
      }
    }
  }, [tramos]);

  // Frases confirmadas que todavía no salieron como tramo. Existe porque el
  // corte natural es el silencio, y un médico puede hablar de corrido y apretar
  // "Detener" sin pausar nunca: sin esto, esa última parrafada se perdería.
  const pendienteRef = useRef("");

  const soltarPendiente = useCallback(() => {
    const texto = pendienteRef.current.trim();
    pendienteRef.current = "";
    if (texto) subirTramo(texto);
  }, [subirTramo]);

  const dg = useDeepgramASR({
    keyterms,
    // Separa en la telemetría las fallas del celular de las del computador:
    // son aparatos, micrófonos y redes distintas, y mezclarlas esconde
    // exactamente el caso que se quiere encontrar.
    origen: "mic_remoto",
    onPartial: setEnVivo,
    onFinal: (texto) => {
      pendienteRef.current = `${pendienteRef.current} ${texto}`.trim();
    },
    onFinDeTramo: () => { setEnVivo(""); soltarPendiente(); },
    // Grabó y no salió nada. Acá importa el doble: el médico está mirando el
    // teléfono, no la ficha, así que si nadie le avisa sigue hablándole a un
    // micrófono muerto mientras cree que el computador se está llenando.
    onSinTexto: () => setError("No se escuchó nada. Revisá que el micrófono del teléfono no esté tapado y tocá de nuevo."),
    onError: (err) => { soltarPendiente(); setError(mensajeErrorMicrofono(err) || "Se cortó el micrófono. Tocá para volver a empezar."); },
    onUnexpectedClose: () => { soltarPendiente(); setError("Se cortó la conexión. Tocá para reanudar."); },
  });

  // Al detener se manda lo que quedó: es el caso de hablar de corrido.
  const detener = useCallback(() => {
    try { dg.stop(); } catch (_) {}
    // Deepgram todavía puede emitir un último final tras el CloseStream, así que
    // se espera un pelo antes de soltar lo acumulado.
    setTimeout(soltarPendiente, 1200);
  }, [dg, soltarPendiente]);

  const empezar = async () => {
    setError("");
    if (!tomado && pedidoRef.current?.id) {
      const { tomado: ok } = await tomarPedido(pedidoRef.current.id);
      if (!ok) {
        setError("El computador canceló el pedido.");
        return;
      }
      setTomado(true);
    }
    await dg.start();
  };

  const terminar = async () => {
    detener();
    // Un respiro para que el último tramo alcance a salir antes de cerrar.
    await new Promise((r) => setTimeout(r, 1600));
    if (pedidoRef.current?.id) await cerrarPedido(pedidoRef.current.id, true);
    onSalir?.();
  };

  const grabando = dg.recording;
  const conectando = dg.status === "connecting";

  return (
    <Box
      data-testid="modo-microfono"
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        bgcolor: "var(--bg-deep)",
        color: "var(--text-primary)",
        display: "flex",
        flexDirection: "column",
        p: 2.5,
        pt: "calc(20px + env(safe-area-inset-top))",
        pb: "calc(20px + env(safe-area-inset-bottom))",
      }}
    >
      {/* Encabezado: a qué computador y a qué paciente le está hablando. Es lo
          que evita que dicte en la ficha equivocada en una sala compartida. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5 }}>
        <PhonelinkRingIcon sx={{ fontSize: 18, color: "var(--color-primary)" }} />
        <Box sx={{ fontSize: 11.5, letterSpacing: 0.8, textTransform: "uppercase", color: "var(--text-faint)" }}>
          Micrófono del computador
        </Box>
      </Box>
      <Box sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
        {pedido?.paciente || `Cama ${pedido?.row ?? ""}`}
      </Box>
      <Box sx={{ fontSize: 13, color: "var(--text-secondary)", mb: 2 }}>
        El texto se escribe donde tengas el cursor en el computador
      </Box>

      {/* Lo dictado hasta ahora. Se ve, pero no se edita: la ficha vive allá. */}
      <Box
        data-testid="tramos-dictados"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          borderRadius: "14px",
          bgcolor: "var(--bg-base)",
          border: "1px solid var(--border-soft)",
          p: 1.75,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        {tramos.length === 0 && !enVivo && (
          <Box sx={{ color: "var(--text-faint)", fontStyle: "italic", fontSize: 14 }}>
            {grabando ? "Escuchando…" : "Tocá el micrófono y hablá."}
          </Box>
        )}
        {tramos.map((t) => (
          <Box key={t.id} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            {t.estado === "enviando" ? (
              <CircularProgress size={13} sx={{ mt: 0.4, color: "var(--text-faint)" }} />
            ) : (
              <CheckIcon
                sx={{
                  fontSize: 15,
                  mt: 0.3,
                  color: t.estado === "falló" ? "var(--color-danger)" : "var(--color-success)",
                }}
              />
            )}
            <Box sx={{ fontSize: 15, lineHeight: 1.45, color: t.estado === "falló" ? "var(--color-danger)" : "var(--text-primary)" }}>
              {t.texto}
            </Box>
          </Box>
        ))}
        {enVivo && (
          <Box sx={{ fontSize: 15, lineHeight: 1.45, color: "var(--text-faint)", fontStyle: "italic" }}>
            {enVivo}
          </Box>
        )}
      </Box>

      {error && (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ fontSize: 13, color: "var(--color-danger)", flex: 1 }}>{error}</Box>
          {tramos.some((t) => t.estado === "falló") && (
            <Button
              data-testid="mic-reintentar"
              size="small"
              onClick={reintentarFallidos}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Reintentar
            </Button>
          )}
        </Box>
      )}

      {/* Botonera. El botón de hablar es enorme a propósito: se toca de pie, con
          una mano, mirando al paciente y no a la pantalla. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2 }}>
        <Button
          data-testid={grabando ? "mic-detener" : "mic-hablar"}
          onClick={grabando ? detener : empezar}
          disabled={conectando}
          variant="contained"
          startIcon={
            conectando ? (
              <CircularProgress size={18} sx={{ color: "inherit" }} />
            ) : grabando ? (
              <StopCircleIcon />
            ) : (
              <MicIcon />
            )
          }
          sx={{
            flex: 1,
            minHeight: 64,
            borderRadius: "999px",
            fontSize: 17,
            fontWeight: 700,
            textTransform: "none",
            bgcolor: grabando ? "var(--color-danger)" : "var(--color-primary)",
            "&:hover": { bgcolor: grabando ? "var(--color-danger)" : "var(--color-primary)" },
            animation: grabando ? "latido 1.5s infinite" : "none",
            "@keyframes latido": {
              "0%,100%": { boxShadow: "0 0 0 0 rgba(var(--color-danger-rgb),0.45)" },
              "70%": { boxShadow: "0 0 0 16px rgba(var(--color-danger-rgb),0)" },
            },
          }}
        >
          {conectando ? "Conectando…" : grabando ? "Detener" : "Hablar"}
        </Button>
        <Button
          data-testid="mic-terminar"
          onClick={terminar}
          sx={{ minHeight: 64, textTransform: "none", color: "var(--text-secondary)", fontSize: 15 }}
        >
          Terminar
        </Button>
      </Box>
    </Box>
  );
};

export default ModoMicrofono;
